/**
 * Fetches information with title item from the CMS API with advanced reactivity support.
 *
 * @async
 * @function useInfosWithTitleItem
 * @param {string | Ref<string>} deliveryKey - The unique key used to retrieve the specific information with title item content.
 * @returns {Promise<Object>} A promise that resolves to the information with title item data fetched from the CMS.
 *
 * @description
 * This composable function retrieves information with title item content from the CMS API with enhanced features:
 * - Supports both static string and reactive reference delivery keys
 * - Uses the current locale path
 * - Includes optional query parameters like 'vse' and 'contentId' from the current route
 * - Sets the fetch request to not be immediate, allowing manual triggering
 * - Supports reactive watching of delivery key changes
 *
 * @example
 * // Fetch information with title item using a static key
 * const { data, pending, error, execute } = await useInfosWithTitleItem('product_details')
 *
 * // Fetch information with title item using a reactive key
 * const dynamicKey = ref('initial_key')
 * const { data, pending, error, execute } = await useInfosWithTitleItem(dynamicKey)
 *
 * @throws {Error} Will throw an error if the API request fails
 *
 * @note
 * - The `immediate: false` option allows more control over when the fetch is executed
 * - Use the `execute()` method to manually trigger the fetch
 * - Supports automatic re-fetching when the delivery key changes if a reactive reference is provided
 */
export async function useInfosWithTitleItem(deliveryKey: string | Ref<string>) {
  const localePath = useLocalePath()
  const key = toRef(() => deliveryKey)

  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey: key.value,
  }

  return useFetch('/api/cms/getInfosWithTitleItem', {
    query,
    immediate: false,
    watch: isRef(deliveryKey) ? [key] : false,
  })
}
